import { useSignal } from "@preact/signals";
import { useMemo } from "preact/hooks";
import { parseScript, scriptSeconds, serializeScript } from "~/protocol/script";
import type { Channel } from "~/web/src/channel";
import { SheetHead } from "~/web/src/project/sheet-head";
import { TextTab } from "~/web/src/project/text-tab";
import { useFile } from "~/web/src/project/use-file";

// The script sheet (mockup ux/06 stage 7): the newest .md as one card per
// scene — number · title · timecode read off the `###` heading, one textarea
// holding the scene's bullets. Save and re-film writes the file back
// (protocol/script.ts round-trips it) and sends the director one chat line;
// it works out what changed itself. The button waits for an idle director: a
// chat text while the explorer asks would answer the ask instead. A file with
// no scene headings falls back to the raw editor.

export function ScriptEditor({
  channel,
  name,
  onClose,
}: {
  channel: Channel;
  name: string;
  onClose: () => void;
}) {
  // null = no edits, the cards follow the disk; else the draft body per scene.
  const bodies = useSignal<string[] | null>(null);
  const file = useFile(channel, name, () => isDirty(bodies.value, file.disk.value));
  const script = useMemo(() => parseScript(file.disk.value), [file.disk.value]);
  const draft = bodies.value ?? script.scenes.map((s) => s.body);
  const dirty = isDirty(bodies.value, file.disk.value);
  const loaded = file.version.value !== 0;

  const agent = channel.agent.value;
  const idle = agent.status === "idle" && channel.connected.value;
  const canSave = dirty && loaded && !file.busy.value && idle;

  async function save() {
    const content = serializeScript({
      head: script.head,
      scenes: script.scenes.map((s, i) => ({ ...s, body: draft[i] ?? s.body })),
    });
    if (!(await file.write(content))) return;
    bodies.value = null;
    const r = await channel.send({
      kind: "chat",
      text: `I edited ${name}. Re-read it, re-cut the EDL and re-render the video.`,
    });
    if (r.ok) onClose();
    else file.notice.value = "saved — the director is busy, tell it when it is done";
  }

  if (loaded && script.scenes.length === 0) {
    return (
      <>
        <SheetHead title="Script" dim={name} onClose={onClose} />
        <div class="sb editor-host">
          <TextTab key={name} channel={channel} name={name} />
        </div>
      </>
    );
  }

  const seconds = scriptSeconds(script);
  const n = script.scenes.length;
  const dim = loaded
    ? `${n} scene${n === 1 ? "" : "s"}${seconds === null ? "" : ` · ${seconds} s`}`
    : null;

  return (
    <>
      <SheetHead title="Script" dim={dim} onClose={onClose} />
      <div class="sb">
        {script.scenes.map((s, i) => {
          const body = draft[i] ?? s.body;
          return (
            <div class={`scene ${body !== s.body ? "editing" : ""}`} key={`${i}-${s.title}`}>
              <div class="h">
                <b>{i + 1}</b>
                <span class="shot">{s.title}</span>
                {s.start && s.end && (
                  <span class="tc">
                    {s.start}–{s.end}
                  </span>
                )}
              </div>
              <textarea
                aria-label={`Scene ${i + 1}`}
                value={body}
                rows={body.split("\n").length + 2}
                spellcheck={false}
                onInput={(e) => {
                  const next = [...draft];
                  next[i] = e.currentTarget.value;
                  bodies.value = next;
                }}
              />
            </div>
          );
        })}
      </div>
      <div class="sf">
        <span class={`note ${file.notice.value ? "notice" : ""}`}>
          {file.notice.value ?? "Saves the script and asks the director to re-cut and re-render."}
        </span>
        <button type="button" class="btn ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          class="btn"
          disabled={!canSave}
          title={dirty && !idle ? "Wait for the director to finish" : undefined}
          onClick={() => void save()}
        >
          Save and re-film
        </button>
      </div>
    </>
  );
}

function isDirty(draft: string[] | null, disk: string): boolean {
  if (!draft) return false;
  const scenes = parseScript(disk).scenes;
  return draft.length !== scenes.length || draft.some((b, i) => b !== scenes[i]?.body);
}
